import { proxy, snapshot, useSnapshot } from 'valtio';

import { devtoolsApi } from '#/dev/devtools';

import { resetStore } from '../common';

export interface SnackbarOptions {
  message: string;
  actionText?: string;
  onAction?: (() => void) | null;
  duration?: number;
}

export interface SnackbarStore {
  queue: SnackbarOptions[];
  isVisible: boolean;
}

export const snackbarStore = proxy<SnackbarStore>({
  queue: [],
  isVisible: false,
});

const initialSnackbarStore = snapshot(snackbarStore) as SnackbarStore;

export const snackbarActions = {
  showSnackbar(snackbar: SnackbarOptions) {
    snackbarStore.queue.push(snackbar);

    snackbarStore.isVisible = !snackbarStore.isVisible;
  },

  /**
   * Removes the current snackbar from the queue and shows the next one if available.
   * This is called internally by the Snackbar component after the exit animation completes.
   * Do not call this directly from outside the Snackbar component.
   */
  removeFromQueue() {
    snackbarStore.queue.shift();

    snackbarStore.isVisible = snackbarStore.queue.length > 0;
  },

  /**
   * Dismisses the current snackbar with animation.
   * This triggers the exit animation and then removes from queue.
   * Use this when you want to programmatically dismiss a snackbar.
   */
  dismissCurrentSnackbar() {
    snackbarStore.isVisible = false;
  },

  reset() {
    resetStore(snackbarStore, initialSnackbarStore);
  },
};

export const useSnackbarStore = () =>
  useSnapshot(snackbarStore) as SnackbarStore;

devtoolsApi.setStore('snackbar', snackbarStore, snackbarActions);
